import React, {useState, useEffect, useRef} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import WsConnector from "core/wsConnector";
import axiApi from "ApiCaller";
import {SwalAlertClass} from "core/swalClass";
import Moment from "moment";
import {useTranslation} from "react-i18next";
import NavBreadcrumb from "components/NavBreadcrum";
import navs from "./navs";
import useCustomTranslation from "core/customTranslation";
import translations from "./translations";

import RecoveryForm from "./forms/RecoveryForm";

const ProcessRecovery = () => {
    const navigate = useNavigate();
    const {processId} = useParams();
    const [processData, setProcessData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liveProgress, setLiveProgress] = useState("");
    const [structuredData, setStructuredData] = useState(null);
    const {t: _tC} = useCustomTranslation(translations);
    const userLang = localStorage.getItem("i18nextLng") || "es";

    const wsConnRef = useRef(null);

    const [_Tr] = useTranslation("routes");
    const bread = [
        {name: _Tr("labels.notifications"), url: "/notifications/", current: false},

        {name: _tC("recovery"), current: true},
    ];

    useEffect(() => {
        if (!processId) {
            setError("ID de proceso no proporcionado");
            setLoading(false);
            return;
        }

        fetchProcessInfo();

        return () => {
            if (wsConnRef.current) {
                wsConnRef.current.close();
            }
        };
    }, [processId]);

    useEffect(() => {
        if (processData) {
            setupWebSocket();
        }

        return () => {
            if (wsConnRef.current) {
                wsConnRef.current.close();
                wsConnRef.current = null;
            }
        };
    }, [processData?.notify?.params?.ended, processData?.notify?.params?.task_id]);

    const fetchProcessInfo = async () => {
        try {
            const resp = await axiApi.get(`/api/notify/get/process/?id=${processId}&lang=${userLang}`);

            if (resp.data.status) {
                setProcessData(resp.data.process);
            }
        } catch (e) {
            if (e.response) {
                navigate("/notifications/");
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    const setupWebSocket = () => {
        const isActive = isProcessActive();

        // Cerrar conexión existente
        if (wsConnRef.current) {
            wsConnRef.current.close();
            wsConnRef.current = null;
        }

        // Solo crear y conectar si el proceso está activo
        if (isActive) {
            console.log("ProcessRecovery: Creating new WebSocket connection");
            const wsConn = new WsConnector();
            wsConn.setUrl(`/ws/tasknotify/${processId}/`);

            wsConn.opened = () => {
                console.log("ProcessRecovery: WebSocket connected successfully");
            };

            wsConn.error = (error) => {
                console.error("ProcessRecovery: WebSocket error:", error);
            };

            wsConn.closered = (event) => {
                console.log("ProcessRecovery: WebSocket closed:", event);
            };

            wsConn.message = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleWebSocketMessage(data);
                } catch (err) {
                    navigate(`/notifications/`);
                    console.error("error:", err);
                }
            };

            wsConn.connect();
            wsConnRef.current = wsConn;
            console.log("ProcessRecovery: WebSocket connection initiated");
        } else {
            console.log("ProcessRecovery: Process not active, WebSocket not needed");
        }
    };

    const handleWebSocketMessage = (data) => {
        if (data.type === "tasknotify.message") {
            const message = data.message || "";
            setLiveProgress(message);

            if (data.data) {
                setStructuredData(data.data);
            }

            let calculatedPercent = 0;

            if (data.data && data.data.dev_processed !== undefined && data.data.devices !== undefined) {
                calculatedPercent = data.data.devices > 0 ? Math.round((data.data.dev_processed / data.data.devices) * 100) : 0;
            }

            const isFinished = data.ended || (message && message.includes("- - Finalizado - -"));

            if (isFinished) {
                setProcessData((prevData) => ({
                    ...prevData,
                    notify: {
                        ...prevData.notify,
                        params: {
                            ...prevData.notify.params,
                            ended: true,
                        },
                    },
                }));

                if (wsConnRef.current) {
                    wsConnRef.current.close();
                    wsConnRef.current = null;
                }
                fetchProcessInfo();
            }
        } else {
            console.log("Error type:", data.type);
        }
    };

    const isProcessActive = () => {
        const active =
            processData &&
            processData.notify &&
            processData.notify.params &&
            processData.notify.params.task_id &&
            !processData.notify.params.ended;

        return active;
    };

    const getNotifyText = () => {
        if (!processData?.notify?.texts) return null;
        const texts = processData.notify.texts;
        return texts.find((t) => t.language === userLang) || texts[0];
    };

    const getTitle = () => {
        const type = processData?.notify?.params?.process_type || "default";
        const titles = {
            recovery_sigfox: _tC("recovery_sigfox"),
            default: _tC("process_monitor"),
        };
        return titles[type] || _tC("process_monitor");
    };

    const getProcessIcon = () => {
        const type = processData?.notify?.params?.process_type || "default";
        const icons = {
            recovery_sigfox: "satellite-dish",
            default: "satellite-dish",
        };
        return icons[type] || "satellite-dish";
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-md-6 text-center mt-5">
                        <p className="mt-2">{_tC("loading")}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return <>{navigate(`/notifications/`)}</>;
    }

    return (
        <>
            <NavBreadcrumb navs={navs} bread={bread} />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 preview-canvas offcanvas-header mt-1">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div className="d-flex align-items-center">
                                <div className="preview-icon">
                                    <FontAwesomeIcon icon={getProcessIcon()} className="me-2" />
                                </div>
                                <div className="preview-title ps-4">
                                    <h5>{getTitle()}</h5>
                                </div>
                                <p className="ms-3 mb-0 text-muted" style={{fontSize: "0.8rem"}}>
                                    {processData?.notify?.date_created
                                        ? Moment(processData.notify.date_created).format("DD/MM/YYYY HH:mm:ss")
                                        : ""}
                                </p>
                            </div>
                            <div className="mt-3">
                                <p>
                                    <span className={`badge fs-6 ${isProcessActive() ? "bg-primary" : "bg-success"}`}>
                                        {isProcessActive() ? _tC("in_progress") : _tC("finished")}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <RecoveryForm liveProgress={liveProgress} structuredData={structuredData} _tC={_tC} />
                </div>
            </div>
        </>
    );
};

export default ProcessRecovery;
